import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, BookOpen, Compass, Sprout, HeartHandshake } from "lucide-react";

const STORAGE_KEY = "lwn_welcome_seen_v2";

const CARDS = [
  {
    id: "coaching",
    icon: HeartHandshake,
    eyebrow: "Signature · 4 Pathways",
    title: "Coaching Programs",
    copy: "The Reset Experience. Go Get Your Crown. Book Coaching. The Birthing Room — pick the one that meets you where you are.",
    cta: "Explore Programs",
    to: "/transform",
    tint: "#DCBB6C",
  },
  {
    id: "gamechanger",
    icon: BookOpen,
    eyebrow: "Available Now · The Book",
    title: "The Gamechanger",
    copy: "The book that turns wounds into weapons and setbacks into strategy.",
    cta: "Buy the Book",
    to: "https://www.gamechangerbook.online",
    external: true,
    tint: "#C77E88",
  },
  {
    id: "lifeprints",
    icon: Compass,
    eyebrow: "Signature · 10 Domains",
    title: "LifePrints™ Assessment",
    copy: "Discover the design of your life across purpose, wholeness, faith, and more — with an AI insight report.",
    cta: "Take LifePrints",
    to: "/assessment",
    tint: "#8FA88B",
  },
  {
    id: "bladegrain",
    icon: Sprout,
    eyebrow: "New · 9 Spiritual Steps",
    title: "The Blade & The Grain",
    copy: "30 statements. Where are you in the deliverance-healing-reigning journey? The mirror doesn't flatter.",
    cta: "Begin Blade & Grain",
    to: "/blade-grain",
    tint: "#C9A227",
  },
];

export default function WelcomePopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), 4500);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
          onClick={close}
          data-testid="welcome-popup"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 200, damping: 22 }}
            className="relative w-full max-w-5xl bg-[#1E3A2B] text-[#FAF4E9] border border-[#DCBB6C]/30 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="welcome-popup-close"
              onClick={close}
              aria-label="Close welcome popup"
              className="absolute top-4 right-4 text-[#FAF4E9]/60 hover:text-[#FAF4E9] z-10 p-2"
            ><X size={20} /></button>

            <div className="px-8 pt-12 pb-6 text-center">
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#DCBB6C]">Welcome, friend</p>
              <h2 className="font-serif-d text-4xl sm:text-5xl mt-4 leading-tight">
                Where would you like to <span className="italic-emph text-[#DCBB6C]">start</span>?
              </h2>
              <p className="mt-5 max-w-xl mx-auto text-sm sm:text-base text-[#FAF4E9]/70">
                Pick the doorway that&apos;s calling you loudest today. Every one of them changes the room you&apos;re living in.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 px-6 pb-8">
              {CARDS.map((c) => {
                const Icon = c.icon;
                const commonClass = "group border border-[#FAF4E9]/15 hover:border-[#DCBB6C] transition-colors p-6 flex flex-col";
                const inner = (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="w-11 h-11 flex items-center justify-center" style={{ background: `${c.tint}22`, color: c.tint }}>
                        <Icon size={22} />
                      </div>
                      <span className="text-[9px] uppercase tracking-[0.22em]" style={{ color: c.tint }}>{c.eyebrow}</span>
                    </div>
                    <h3 className="mt-5 font-serif-d text-2xl">{c.title}</h3>
                    <p className="mt-3 text-sm text-[#FAF4E9]/65 flex-1">{c.copy}</p>
                    <span className="mt-6 text-[11px] uppercase tracking-[0.22em] group-hover:translate-x-1 transition-transform" style={{ color: c.tint }}>
                      {c.cta} →
                    </span>
                  </>
                );
                if (c.external) {
                  return (
                    <a
                      key={c.id}
                      href={c.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={close}
                      data-testid={`welcome-cta-${c.id}`}
                      className={commonClass}
                    >{inner}</a>
                  );
                }
                return (
                  <Link
                    key={c.id}
                    to={c.to}
                    onClick={close}
                    data-testid={`welcome-cta-${c.id}`}
                    className={commonClass}
                  >{inner}</Link>
                );
              })}
            </div>

            <div className="border-t border-[#FAF4E9]/10 px-8 py-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-[#FAF4E9]/50">Not sure? Just browse — I&apos;ll be here.</p>
              <button
                data-testid="welcome-popup-dismiss"
                onClick={close}
                className="text-[11px] uppercase tracking-[0.22em] text-[#FAF4E9]/60 hover:text-[#DCBB6C]"
              >Keep browsing →</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
